/**
 * Bir hareketin veya işlemin kaynağına gitme yolu.
 *
 * Fiş, fatura, reçete ve takas detayları kendi sayfalarında yaşıyor; uygulama genelinde
 * açılabilen bir sağlayıcıları yok. Bu yüzden kaynağa gitmek, hedef sayfanın zaten okuduğu
 * derin bağlantı parametresiyle yapılır: eczacı belgeyi her zaman göreceği yerde görür,
 * hareket ekranı için ikinci bir detay ekranı çoğaltılmaz.
 */

export type HareketKaynakTuru = 'satis' | 'fatura' | 'recete' | 'takas' | 'siparis'

export type HareketKaynagi = {
  tur: HareketKaynakTuru
  /** Menüde görünen eylem metni. */
  etiket: string
  /** Hedef adres; sipariş sepeti gibi uygulama içi panellerde null olur. */
  adres: string | null
}

/** Hareket satırından kaynak çıkarmak için gereken alanlar; hepsi isteğe bağlı. */
export type HareketKaynakGirdisi = {
  saleId?: string | null
  saleNumber?: string | null
  prescriptionId?: string | null
  referenceType?: string | null
  referenceId?: string | null
  counterpartyGln?: string | null
  documentNumber?: string | null
}

const UUID_KALIBI = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

const FATURA_REFERANSLARI = new Set(['DEPOT_INVOICE', 'MANUAL_DEPOT_INVOICE'])

function temiz(deger: string | null | undefined): string | null {
  const metin = (deger ?? '').trim()
  return metin.length > 0 ? metin : null
}

export function satisAdresi(saleId: string): string {
  return `/satis/gecmis?satis=${encodeURIComponent(saleId)}`
}

export function faturaAdresi(invoiceId: string): string {
  return `/depo?sekme=faturalar&fatura=${encodeURIComponent(invoiceId)}`
}

export function receteAdresi(prescriptionId: string): string {
  return `/listeler/receteler?recete=${encodeURIComponent(prescriptionId)}`
}

export function takasAdresi(partnerGln: string): string {
  return `/takaslar/hareketler?partner=${encodeURIComponent(partnerGln)}`
}

/**
 * Bir stok hareketinin gidilebilir kaynakları.
 *
 * Tek satırda birden fazla kaynak olabilir: reçeteli bir satışta hem fiş hem reçete
 * anlamlıdır ve eczacı anlaşmazlığa göre birine ya da ötekine bakar.
 */
export function hareketKaynaklari(satir: HareketKaynakGirdisi): HareketKaynagi[] {
  const kaynaklar: HareketKaynagi[] = []

  const satisId = temiz(satir.saleId)
  if (satisId) {
    const fis = temiz(satir.saleNumber)
    kaynaklar.push({
      tur: 'satis',
      etiket: fis ? `${fis} numaralı satış fişini aç` : 'Satış fişini aç',
      adres: satisAdresi(satisId),
    })
  }

  const receteId = temiz(satir.prescriptionId)
  if (receteId && UUID_KALIBI.test(receteId)) {
    kaynaklar.push({ tur: 'recete', etiket: 'Reçeteyi aç', adres: receteAdresi(receteId) })
  }

  const referansTuru = (temiz(satir.referenceType) ?? '').toUpperCase()
  const referansId = temiz(satir.referenceId)
  if (referansId && UUID_KALIBI.test(referansId) && FATURA_REFERANSLARI.has(referansTuru)) {
    const belgeNo = temiz(satir.documentNumber)
    kaynaklar.push({
      tur: 'fatura',
      etiket: belgeNo ? `${belgeNo} numaralı faturayı aç` : 'Depo faturasını aç',
      adres: faturaAdresi(referansId),
    })
  }

  const partnerGln = temiz(satir.counterpartyGln)
  if (partnerGln) {
    kaynaklar.push({ tur: 'takas', etiket: 'Takas hareketlerini aç', adres: takasAdresi(partnerGln) })
  }

  return kaynaklar
}

/**
 * İşlem defteri satırının kaynağı. Sunucu bağlantı türünü ve kimliğini hazır verdiği için
 * burada yalnızca adres kurulur.
 */
export function islemKaynaklari(islem: {
  baglanti_turu?: string | null
  baglanti_id?: string | null
  referans?: string | null
}): HareketKaynagi[] {
  const tur = (temiz(islem.baglanti_turu) ?? '').toLowerCase()
  const kimlik = temiz(islem.baglanti_id)
  const referans = temiz(islem.referans)

  if (tur === 'satis' && kimlik) {
    return [
      {
        tur: 'satis',
        etiket: referans ? `${referans} numaralı satış fişini aç` : 'Satış fişini aç',
        adres: satisAdresi(kimlik),
      },
    ]
  }

  if (tur === 'recete' && kimlik && UUID_KALIBI.test(kimlik)) {
    return [{ tur: 'recete', etiket: 'Reçeteyi aç', adres: receteAdresi(kimlik) }]
  }

  if (tur === 'fatura' && kimlik && UUID_KALIBI.test(kimlik)) {
    return [
      {
        tur: 'fatura',
        etiket: referans ? `${referans} numaralı faturayı aç` : 'Depo faturasını aç',
        adres: faturaAdresi(kimlik),
      },
    ]
  }

  if (tur === 'takas' && kimlik) {
    return [{ tur: 'takas', etiket: 'Takas hareketlerini aç', adres: takasAdresi(kimlik) }]
  }

  return []
}
